import React from 'react';
import {
  Table,
  TableRow,
  TableCell,
  Type,
  TableSortIcon,
} from '@spotify-internal/encore-creator-web';
import { white, black } from '@spotify-internal/encore-foundation';
import { TableHeaderCell, TableHeaderLabel } from '../../Shared/Table';
import {
  EpisodeRow,
  PodcastThumbnail,
  PodcastImageWrapper,
  PodcastImage,
  Checkmark,
} from './shared/styles';
import { Trailer, Episode } from '../../../types';
import { SortColumn, SortOrder, SortColumnState } from './shared/types';
import { formatDate, formatTimestamp } from '../../../lib/Formatter';

type Props = {
  episodes?: Episode[];
  episodesTotal?: number;
  sort: SortColumnState;
  selectedEpisode?: Trailer;
  handleEpisodeClick(episode: Episode): void;
  handleColumnSortClick(column: SortColumn): void;
};

export function AllEpisodesTable(props: Props) {
  const {
    episodes,
    episodesTotal,
    sort,
    selectedEpisode,
    handleEpisodeClick,
    handleColumnSortClick,
  } = props;

  if (!episodes) return <></>; // suppress PropTypes error from react-infinite-scroller

  const tableSortIconDirection = (sortOrder: SortOrder) => {
    return sortOrder === SortOrder.ASC ? 'up' : 'down';
  };

  return (
    <Table>
      <colgroup>
        <col />
        <col width="100" />
        <col width="160" />
      </colgroup>
      <thead>
        <TableRow>
          <TableHeaderCell>{`${episodesTotal} ${
            episodesTotal === 1 ? 'Episode' : 'Episodes'
          }`}</TableHeaderCell>
          <TableHeaderCell align="right" onClick={() => handleColumnSortClick(SortColumn.LENGTH)}>
            <TableHeaderLabel active={sort.column === SortColumn.LENGTH} label="Length">
              {sort.column === SortColumn.LENGTH && (
                <TableSortIcon direction={tableSortIconDirection(sort.order)} />
              )}
            </TableHeaderLabel>
          </TableHeaderCell>
          <TableHeaderCell
            align="right"
            onClick={() => handleColumnSortClick(SortColumn.PUBLISH_DATE)}
          >
            <TableHeaderLabel active={sort.column === SortColumn.PUBLISH_DATE} label="Publish Date">
              {sort.column === SortColumn.PUBLISH_DATE && (
                <TableSortIcon direction={tableSortIconDirection(sort.order)} />
              )}
            </TableHeaderLabel>
          </TableHeaderCell>
        </TableRow>
      </thead>
      <tbody>
        {episodes.map(episode => (
          <EpisodeRow
            key={`short-episodes--${episode.id}`}
            hover
            active={selectedEpisode && selectedEpisode.id === episode.id}
            onClick={() => {
              handleEpisodeClick(episode);
            }}
            data-testid="trailer-selection-modal--table-row"
          >
            <TableCell>
              <PodcastThumbnail>
                <PodcastImageWrapper>
                  {selectedEpisode && selectedEpisode.id === episode.id && (
                    <Checkmark
                      aria-label="Selected episode"
                      color={white}
                      style={{ position: 'absolute' }}
                    />
                  )}
                  <PodcastImage
                    src={episode.artworkUrl}
                    alt="Podcast Artwork"
                    imageWidth="48px"
                    imageHeight="48px"
                  />
                </PodcastImageWrapper>
                <Type as="span" variant={Type.body2} weight={Type.bold} color={black}>
                  {episode.name}
                </Type>
              </PodcastThumbnail>
            </TableCell>
            <TableCell align="right" numerical>
              {formatTimestamp(episode.duration / 1000)}
            </TableCell>
            <TableCell align="right" numerical>
              {formatDate(episode.releaseDate, 'D MMM YYYY')}
            </TableCell>
          </EpisodeRow>
        ))}
      </tbody>
    </Table>
  );
}
